import { useState, useEffect, useMemo } from 'react'
import { Outlet, useNavigate, useLocation } from 'react-router-dom'
import api from '../../core/services/api'
import { useAuthStore, getSavedTenantCode } from '../../core/stores/authStore'
import { useMaiaStore } from '../../core/stores/maiaStore'
import { useMenuLayoutStore } from '../../core/stores/menuLayoutStore'
import { useWakeWord } from '../../hooks/useWakeWord'
import { navigation } from '../../core/navigation'
import SidebarMenu from '../../components/menu/SidebarMenu'
import RadialCoronaMenu from '../../components/menu/RadialCoronaMenu'
import CommandPaletteMenu from '../../components/menu/CommandPaletteMenu'
import FloatingDockMenu from '../../components/menu/FloatingDockMenu'
import MaiaChatButton from '../../components/maia/MaiaChatButton'
import MaiaChatPanel from '../../components/maia/MaiaChatPanel'
import NotificationBell from '../../components/NotificationBell'

export default function TenantLayout() {
  const { user, logout } = useAuthStore()
  const { toggle: maiaToggle, isOpen: maiaIsOpen, wakeWordEnabled, checkMicPermission, openWithVoice } = useMaiaStore()
  const { layout } = useMenuLayoutStore()
  const navigate = useNavigate()
  const location = useLocation()
  const [scadenzeBadge, setScadenzeBadge] = useState(0)
  const [approvazioniBadge, setApprovazioniBadge] = useState(0)

  useEffect(() => {
    checkMicPermission()
  }, [checkMicPermission])

  useWakeWord({
    enabled: wakeWordEnabled && !maiaIsOpen,
    onWake: () => openWithVoice(),
  })

  // Ctrl+M per aprire/chiudere ELETTRA
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.key === 'm') {
        e.preventDefault()
        maiaToggle()
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [maiaToggle])

  useEffect(() => {
    const fetchBadges = async () => {
      try {
        const [scadRes, apprRes] = await Promise.all([
          api.get('/v1/scadenziario/conteggio'),
          api.get('/v1/approvazioni/count').catch(() => ({ data: { count: 0 } })),
        ])
        setScadenzeBadge(scadRes.data.conteggio || 0)
        setApprovazioniBadge(apprRes.data.count || 0)
      } catch { /* ignore */ }
    }
    fetchBadges()
    const interval = setInterval(fetchBadges, 5 * 60 * 1000)
    return () => clearInterval(interval)
  }, [location.pathname])

  const navWithBadge = useMemo(() => {
    return navigation.map(item => {
      if (item.name === 'Scadenziario') return { ...item, badge: scadenzeBadge }
      if (item.name === 'Approvazioni') return { ...item, badge: approvazioniBadge }
      return item
    })
  }, [scadenzeBadge, approvazioniBadge])

  const handleLogout = async () => {
    await logout()
    const tenantCode = getSavedTenantCode()
    navigate(tenantCode ? `/${tenantCode}` : '/')
  }

  const menuProps = { navigation: navWithBadge, user, onLogout: handleLogout }

  const mainClassName = layout === 'sidebar'
    ? 'ml-64 p-8'
    : layout === 'dock'
      ? 'p-8 pb-24'
      : 'p-8'

  return (
    <div className="min-h-screen bg-gray-50">
      {layout === 'sidebar' && <SidebarMenu {...menuProps} />}
      {layout === 'radial' && <RadialCoronaMenu {...menuProps} />}
      {layout === 'command' && <CommandPaletteMenu {...menuProps} />}
      {layout === 'dock' && <FloatingDockMenu {...menuProps} />}

      <main className={mainClassName}>
        <div className="flex justify-end mb-2">
          <NotificationBell />
        </div>
        <Outlet />
      </main>

      <MaiaChatButton />
      <MaiaChatPanel />
    </div>
  )
}
